// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import Hidden from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import { isTabletScreen } from "@steiner/XDL/Utilities";
import React, { FC, useEffect, useRef, useState } from "react";

interface Props {
    id: string;
}

export interface HeaderLayoutStyles {
    container?: InjectableCss;
    logoWrapper?: InjectableCss;
    secondaryMainNavigation?: InjectableCss;
    searchMenuWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    linkListWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
}

export const headerLayoutStyles: HeaderLayoutStyles = {};

const styles = headerLayoutStyles;
const HeaderLayout: FC<Props> = ({ id }) => {
    const { isInShell } = useShellContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let searchBarOffsetTop = searchRef.current?.offsetTop || 0;
        const fixedHeight = 45;
        const buffer = 13; // Adjust this buffer as needed for pixel-perfect alignment

        const handleScroll = () => {
            if (!searchRef.current || !containerRef.current || !isTabletScreen()) {
                return;
            }

            searchBarOffsetTop = containerRef.current.className.includes("str-header-sticky")
                ? searchBarOffsetTop
                : searchRef.current?.offsetTop;

            const currentScroll = window.scrollY;
            const stickyThreshold = searchBarOffsetTop - fixedHeight + buffer;

            if (currentScroll >= stickyThreshold) {
                containerRef.current?.classList.add("str-header-sticky");
            } else {
                containerRef.current?.classList.remove("str-header-sticky");
            }
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const isShareEntity = getIsShareEntity();
    return (
        <StyledWrapper {...styles.container} ref={containerRef} {...{ isInShell }}>
            <StyledWrapper data-test-selector="logo" {...styles.logoWrapper} {...{ isInShell }}>
                <XngZone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <StyledWrapper
                {...styles.searchMenuWrapper}
                data-test-selector="search"
                ref={searchRef}
                className="search-sticky"
                {...{ isInShell }}
            >
                <XngZone contentId={id} zoneName="Search" />
            </StyledWrapper>
            <StyledWrapper
                {...styles.actionsWrapper}
                data-test-selector="actions"
                className="action-sticky"
                {...{ isInShell }}
            >
                <StyledWrapper className="mainNavigation-wrapper">
                    {!isShareEntity && <XngZone contentId={id} zoneName="MainNavigation" normalWidth={true} />}
                </StyledWrapper>
                <StyledWrapper className="extraMenu-wrapper">
                    <XngZone contentId={id} zoneName="LinkList" hint="Extra Menu" normalWidth={true} />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="SignIn" normalWidth={true} />
                <XngZone contentId={id} zoneName="MiniCart" normalWidth={true} />
            </StyledWrapper>
            <StyledWrapper
                {...styles.secondaryMainNavigation}
                data-test-selector="secondary-main-navigation"
                {...{ isInShell }}
            >
                <XngZone contentId={id} zoneName="SecondaryMainNavigation" normalWidth={true} />
            </StyledWrapper>
            <StyledWrapper {...styles.mainNavigationWrapper} data-test-selector="main-navigation" {...{ isInShell }}>
                {!isShareEntity && <XngZone contentId={id} zoneName="MainNavigation" normalWidth={true} />}
            </StyledWrapper>
            <StyledWrapper data-test-selector="linklist" {...styles.linkListWrapper} {...{ isInShell }}>
                <XngZone contentId={id} zoneName="MainNavigationRightSection" hint="link-list" normalWidth={true} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default HeaderLayout;
